import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {Bell, Plus, Trash2, Edit3, X, CheckCircle2, Pause, Play, MapPin, Award, ArrowRight, Sparkles, AlertCircle, Mail, Clock, Calendar, Send, Loader2} from 'lucide-react';
import {
  useCandidateAlerts,
  CANDIDATE_ALERT_QUALIFICATIONS,
  type CandidateAlert,
  type NewCandidateAlert,
  type CandidateAlertFrequency,
  type CandidateAlertQualification,
} from "@/hooks/useCandidateAlerts";
import { FRENCH_REGIONS } from "@/hooks/useAvailableCandidates";
import { testCandidateAlertNow } from "@/lib/candidateAlertsService";

const FREQUENCY_LABELS: Record<CandidateAlertFrequency, string> = {
  immediate: "Immédiate",
  daily: "Quotidienne",
  weekly: "Hebdomadaire",
};

const FREQUENCY_DESCRIPTIONS: Record<CandidateAlertFrequency, string> = {
  immediate: "Notification dès qu'un nouveau candidat correspond",
  daily: "Récapitulatif tous les jours",
  weekly: "Récapitulatif chaque semaine",
};

interface Props {
  recruiterId: string;
  recruiterEmail?: string;
  initialDraft?: Partial<NewCandidateAlert>;
}

const emptyDraft: NewCandidateAlert = {
  name: "",
  qualification: "ALL",
  region: "",
  frequency: "daily",
  isActive: true,
};

function formatDate(iso?: string) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
}

export default function CandidateAlertsManager({ recruiterId, recruiterEmail, initialDraft }: Props) {
  const { alerts, loading, error, fetchMine, createAlert, updateAlert, deleteAlert } =
    useCandidateAlerts();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CandidateAlert | null>(null);
  const [draft, setDraft] = useState<NewCandidateAlert>({ ...emptyDraft });
  const [submitting, setSubmitting] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [diagnostic, setDiagnostic] = useState<{
    alertName: string;
    totalCandidates: number;
    matched: number;
    emailSent: boolean;
    emailError?: string;
    topRejections: string[];
  } | null>(null);

  // Charger les alertes au montage
  useEffect(() => {
    if (recruiterId) {
      fetchMine(recruiterId);
    }
  }, [recruiterId, fetchMine]);

  // Si on reçoit un brouillon initial (depuis /disponibles), pré-remplir le formulaire
  useEffect(() => {
    if (initialDraft && Object.keys(initialDraft).length > 0) {
      setDraft({ ...emptyDraft, ...initialDraft });
      setEditing(null);
      setShowForm(true);
    }
  }, [initialDraft]);

  const sortedAlerts = useMemo(
    () => [...alerts].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")),
    [alerts]
  );

  const openCreate = () => {
    setEditing(null);
    setDraft({ ...emptyDraft });
    setShowForm(true);
  };

  const openEdit = (a: CandidateAlert) => {
    setEditing(a);
    setDraft({
      name: a.name,
      qualification: a.qualification,
      region: a.region || "",
      frequency: a.frequency,
      isActive: a.isActive,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setDraft({ ...emptyDraft });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) {
      toast.error("Veuillez donner un nom à votre alerte.");
      return;
    }
    setSubmitting(true);
    try {
      // Payload simplifié : on neutralise les anciens critères pour qu'une mise à jour
      // d'une vieille alerte les retire effectivement (matching = Diplôme + Région).
      const payload: NewCandidateAlert = {
        name: draft.name.trim(),
        qualification: draft.qualification,
        region: draft.region?.trim() || "",
        frequency: draft.frequency,
        isActive: draft.isActive,
        department: "",
        city: "",
        mobility: "any",
        housingNeed: "any",
        specialty: "",
      };
      if (editing) {
        await updateAlert(editing._id, payload);
        toast.success("Alerte mise à jour");
      } else {
        await createAlert(recruiterId, recruiterEmail, payload);
        toast.success("Alerte candidat créée — vous serez notifié(e) par email");
      }
      closeForm();
    } catch (err) {
      console.error(err);
      toast.error("Échec de l'enregistrement de l'alerte.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (a: CandidateAlert) => {
    try {
      await updateAlert(a._id, { isActive: !a.isActive });
      toast.success(a.isActive ? "Alerte mise en pause" : "Alerte réactivée");
    } catch {
      toast.error("Action impossible.");
    }
  };

  const handleDelete = async (a: CandidateAlert) => {
    if (!confirm(`Supprimer définitivement l'alerte « ${a.name} » ?`)) return;
    try {
      await deleteAlert(a._id);
      toast.success("Alerte supprimée");
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const handleTestNow = async (a: CandidateAlert) => {
    if (!recruiterEmail) {
      toast.error("Email recruteur introuvable — reconnectez-vous.");
      return;
    }
    setTestingId(a._id);
    setDiagnostic(null);
    try {
      const res = await testCandidateAlertNow({
        alert: a,
        recruiterEmail,
      });
      setDiagnostic({
        alertName: a.name,
        totalCandidates: res.totalCandidates,
        matched: res.matched,
        emailSent: res.emailSent,
        emailError: res.emailError,
        topRejections: res.topRejections,
      });
      if (res.emailSent) {
        toast.success(`Email test envoyé — ${res.matched} candidat(s) correspondant(s)`);
        await fetchMine(recruiterId);
      } else if (res.matched > 0 && res.emailError) {
        toast.error(`${res.matched} candidat(s) trouvé(s) mais email KO : ${res.emailError}`);
      } else {
        toast(`Aucun candidat ne correspond — voir le diagnostic ci-dessous`, {
          icon: "ℹ️",
          duration: 5000,
        });
      }
    } catch (e) {
      console.error(e);
      toast.error("Test impossible — voir la console.");
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header bloc */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-blue-100 rounded-2xl p-5 sm:p-6">
        <div className="flex items-start sm:items-center justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
              <Bell className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                Alertes candidats — Disponibles immédiatement
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Soyez notifié(e) par email dès qu'un candidat correspondant à votre diplôme et région devient disponible (CVthèque 48h).
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            Nouvelle alerte
          </button>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Diagnostic du dernier test */}
      {diagnostic && (
        <div
          className={`rounded-xl border p-4 text-sm ${
            diagnostic.emailSent
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : diagnostic.matched > 0
                ? "bg-amber-50 border-amber-200 text-amber-900"
                : "bg-blue-50 border-blue-200 text-blue-900"
          }`}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="font-bold">
              Diagnostic — alerte « {diagnostic.alertName} »
            </div>
            <button
              type="button"
              onClick={() => setDiagnostic(null)}
              className="p-0.5 hover:bg-black/5 rounded"
              aria-label="Fermer le diagnostic"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <ul className="space-y-1 text-[13px] leading-relaxed">
            <li>
              <strong>{diagnostic.totalCandidates}</strong> candidat(s) actuellement publié(s) dans la CVthèque (dispo 48h).
            </li>
            <li>
              <strong>{diagnostic.matched}</strong> correspond(ent) à vos critères.
            </li>
            <li>
              {diagnostic.emailSent ? (
                <>✓ Email envoyé à votre adresse recruteur.</>
              ) : diagnostic.matched > 0 ? (
                <>⚠️ Email non envoyé : {diagnostic.emailError || "raison inconnue"}</>
              ) : (
                <>Aucun email — aucun candidat ne correspond aux critères.</>
              )}
            </li>
          </ul>
          {diagnostic.topRejections.length > 0 && (
            <div className="mt-3 pt-3 border-t border-current/10">
              <div className="text-[11px] font-bold uppercase tracking-wide opacity-70 mb-1">
                Pourquoi les candidats ne matchent pas
              </div>
              <ul className="space-y-1 text-[12px]">
                {diagnostic.topRejections.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="mt-1 w-1 h-1 rounded-full bg-current opacity-60 flex-shrink-0" />
                    <span className="break-words">{r}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] opacity-75 italic">
                Astuce : élargissez vos critères (« Tous les diplômes » ou « Toutes les régions »).
              </p>
            </div>
          )}
        </div>
      )}

      {/* Liste */}
      {loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500">
          Chargement de vos alertes…
        </div>
      )}

      {!loading && sortedAlerts.length === 0 && (
        <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-10 text-center">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-900">Aucune alerte candidat configurée</h3>
          <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
            Créez votre première alerte pour recevoir un email dès qu'un BNSSA, MNS, BPJEPS AAN, BEESAN ou STAPS correspondant à votre diplôme et région devient disponible immédiatement.
          </p>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 mt-5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Créer une alerte
          </button>
        </div>
      )}

      {!loading && sortedAlerts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sortedAlerts.map((a) => (
            <article
              key={a._id}
              className={`bg-white border rounded-2xl p-5 transition-shadow ${
                a.isActive ? "border-gray-200 shadow-sm hover:shadow-md" : "border-gray-200 opacity-75"
              }`}
            >
              <header className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-gray-900 break-words">{a.name}</h3>
                    {a.isActive ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-500 border border-gray-200 px-2 py-0.5 rounded-full">
                        En pause
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1.5 flex-wrap">
                    <Calendar className="w-3 h-3" />
                    Créée le {formatDate(a.createdAt)}
                    {a.lastSentAt && (
                      <>
                        <span className="text-gray-300">•</span>
                        <Mail className="w-3 h-3" />
                        Dernier envoi : {formatDate(a.lastSentAt)}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleTestNow(a)}
                    disabled={testingId === a._id}
                    className="p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 rounded"
                    aria-label="Tester maintenant"
                    title="Tester maintenant : envoyer un email récap immédiatement si des candidats correspondent"
                  >
                    {testingId === a._id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggle(a)}
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
                    aria-label={a.isActive ? "Mettre en pause" : "Réactiver"}
                    title={a.isActive ? "Mettre en pause" : "Réactiver"}
                  >
                    {a.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(a)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                    aria-label="Éditer"
                    title="Éditer"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(a)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    aria-label="Supprimer"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </header>

              {/* Critères affichés : Diplôme · Fréquence · Région */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div className="flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                  <Award className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Diplôme</div>
                    <div className="text-gray-800 font-semibold break-words">
                      {a.qualification === "ALL" ? "Tous" : a.qualification}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                  <Clock className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Fréquence</div>
                    <div className="text-gray-800 font-semibold break-words">
                      {FREQUENCY_LABELS[a.frequency]}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                  <MapPin className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Région</div>
                    <div className="text-gray-800 font-semibold break-words">
                      {a.region?.trim() ? a.region : "Toutes"}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Modal création / édition */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={closeForm}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-5 flex items-center justify-between z-10">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                {editing ? "Modifier l'alerte candidat" : "Nouvelle alerte candidat"}
              </h2>
              <button
                onClick={closeForm}
                aria-label="Fermer"
                className="p-1.5 hover:bg-gray-100 rounded-md"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Nom de l'alerte <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Ex : MNS dispo en Île-de-France"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Diplôme + Fréquence */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5" />
                    Diplôme
                  </label>
                  <select
                    value={draft.qualification}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        qualification: e.target.value as CandidateAlertQualification,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  >
                    {CANDIDATE_ALERT_QUALIFICATIONS.map((q) => (
                      <option key={q} value={q}>
                        {q === "ALL" ? "Tous les diplômes" : q}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Fréquence d'envoi <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={draft.frequency}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        frequency: e.target.value as CandidateAlertFrequency,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  >
                    <option value="immediate">Immédiate — dès qu'un candidat correspond</option>
                    <option value="daily">Quotidienne — récap chaque jour</option>
                    <option value="weekly">Hebdomadaire — récap chaque semaine</option>
                  </select>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {FREQUENCY_DESCRIPTIONS[draft.frequency]}
                  </p>
                </div>
              </div>

              {/* Localisation : Région uniquement */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  Région
                </label>
                <select
                  value={draft.region || ""}
                  onChange={(e) => setDraft({ ...draft, region: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                >
                  <option value="">Toutes les régions</option>
                  {FRENCH_REGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  Sélectionnez une région française pour cibler les candidats, ou laissez « Toutes ».
                </p>
              </div>

              {/* Actif */}
              <label className="flex items-center gap-2.5 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-800 font-medium">Alerte active dès maintenant</span>
              </label>

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-semibold text-sm text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {editing ? "Mettre à jour" : "Créer l'alerte"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
